import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UploadUtilService } from 'src/app/common/photo-uploader/upload-util.service';
import { TypeParcours } from 'src/app/constantes/global/parcours-model';
import { DocumentReadModel } from 'src/app/models/documents-service/document-read-model';
import { ChangeFormateurBpfStatus, FormateursBpfListe } from 'src/app/models/formateurs/formateurs-bpf.model';
import { ListeFixeDetailleeModel } from 'src/app/models/listes-fixes/liste-fixe-detaillee.model';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { ValeurListeFixeModel } from '../../models/listes-fixes/valeur-liste-fixe.model';
import { ModaliteDeroulementModel } from '../../models/sessions/modalite-deroulement-model';
import { ResumeModel } from '../../models/sessions/resume-session-model';
import { DuplicateSessionModel, SessionDetailleeModel } from '../../models/sessions/session-detaillee-model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { Document } from '../../pages/emails/model/EmailModel';
import { UrlUtils } from '../../utils/url-utils';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

export interface NoSeanceModel {
  hasNoSeance: boolean;
  hasSaisieAbsencesIncomplete: boolean;
}

export interface NoSeanceNoFormateurModel {
  noFormateur: boolean;
  noSeance: boolean;
}

@Injectable({
  providedIn: 'root',
})

export class SessionsService {
  private sessionSource = new BehaviorSubject<SessionDetailleeModel | null>(null);
  currentSession$ = this.sessionSource.asObservable();
    constructor(
    private http: HttpClient,
    private oDataAdaptor: ODataAdaptor,
    private notificationService: NotificationToastService,
    private configService: ConfigService,
    private uploadUtilService: UploadUtilService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  private baseUrl = `${this.configService.config.urlApi}/api/f`;

  private actionUrl = UrlUtils.generateActionUrlFormatter(`${this.baseUrl}/sessions`);

  buildDataManagerGetSessions(idFormation: number): DataManager {
    return new DataManager({
      url: `${this.baseUrl}/sessionsview?IdFormation=${idFormation}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetDocumentsSession(idSession: number): DataManager {
    return new DataManager({
      url: `${this.baseUrl}/sessions/GetDocuments/${idSession}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetEmailsConvocationsFormateurs(idSession) {
    return new DataManager({
      url: `${this.baseUrl}/sessions/GetEmails/${idSession}`,
      adaptor: this.oDataAdaptor,
    });
  }

  getAllSessionIdsByUtilisateur(): Observable<number[]> {
    return this.http.get<number[]>(this.actionUrl('getallsessionidsbyutilisateur'));
  }

  chargerSession(idSession: number): Observable<SessionDetailleeModel> {
    return this.http.get<SessionDetailleeModel>(this.actionUrl('getsession/' + idSession));
  }


  enregistrerModificationsSession(session: SessionDetailleeModel, isParcours: boolean): Observable<void> {
    return this.http
      .post<void>(this.actionUrl('updatesession'), session)
      .pipe(tap(() => {
        isParcours ? this.notificationService.push(new ToastSuccesModel('Le parcours a été modifiée.')) : this.notificationService.push(new ToastSuccesModel('La session a été modifiée.'))
      }));
  }

  creerSession(session: SessionDetailleeModel): Observable<SessionDetailleeModel> {
    const url = this.actionUrl('addsession');
    return this.http.put<number>(url, session).pipe(
      tap(id => (session.idSession = id)),
      map(() => session),
      tap(() => this.notificationService.push(new ToastSuccesModel('La session a été créée.'))),
    );
  }

  chargerEtatsSession(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrl}/listeFixeetatssession/getvaleurs`);
  }

  chargerModalitesDeDeroulemnt(): Observable<ModaliteDeroulementModel[]> {
    return this.http.get<ModaliteDeroulementModel[]>(`${this.baseUrl}/sessions/getModalitesPossibles`);
  }

  activerDesactiverSession(session: SessionDetailleeModel, activer: boolean) {
    const statusText = activer ? 'activée' : 'désactivée';
    return this.http.post(this.actionUrl('basculerActivation'), { IdSession: session.idSession, IsActive: activer }).pipe(
      tap(() => {
        session.active = activer; // Mise à jour locale de la session
        this.sessionSource.next(session); // Notifie tous les composants abonnés avec la nouvelle session
      }),
      tap(() => this.notificationService.push(new ToastSuccesModel(`La session a été ${statusText}`))),
    );
  }

  televerserDocument(document: File, idSession: number, updateFunction: () => any = null): Observable<DocumentReadModel> {
    return this.uploadUtilService
      .uploadFile(`${this.actionUrl(`AddDocument`)}`, {File: document, idSession}, updateFunction)
      .pipe(tap(() => this.notificationService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  public deleteDocument(doc: Document, nom: string) {    
    return this.http.delete(`${this.baseUrl}/documents/${doc.idDocument}/?idSession=${doc.idSession}`).pipe(
      tap(() => {
        this.notificationService.push(new ToastSuccesModel(`Le document : ${nom} a été supprimé.`));
      })
    );
  }

  getDocumentDownloadURL(idDocument: number) {
    return `${this.actionUrl(`GetDocument/${idDocument}`)}`;
  }

  sessionHasNoSeanceOrAbsencesIncompletes(idSession: number): Observable<NoSeanceModel> {
    return this.http.get<NoSeanceModel>(`${this.actionUrl(`HasNoSeanceOrSaisieAbsenceIncomplete/${idSession}`)}`);
  }

  sessionHasNoSeanceOrNoFormateur(idSession: number): Observable<NoSeanceNoFormateurModel> {
    return this.http.get<NoSeanceNoFormateurModel>(`${this.actionUrl(`hasNoSeanceOrNoFormateur/${idSession}`)}`);
  }

  chargerResume(idSession: number): Observable<ResumeModel> {
    return this.http.get<ResumeModel>(this.actionUrl('getResume/' + idSession));
  }

  isSessionEntitesAutorisees(idSession: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.actionUrl(`isSessionEntitesAutorisees/${idSession}`)}`);
  }

  getListeLieuxApiUrl() {
    return `${this.baseUrl}/LieuxSessionView`
  }

  public getAllListeSession(): Observable<ListeFixeDetailleeModel[]> {
    return this.http.get<ListeFixeDetailleeModel[]>(`${this.baseUrl}/ListesFixes/GetAllSession`);
  }

  public updateSession(session: SessionDetailleeModel) {
    this.sessionSource.next(session);
  }

  // public duplicateSession(session: SessionDetailleeModel): Observable<any> {
  //   let message: string;
  //   switch (session.typeParcours) {
  //     case TypeParcours.PARCOURS:
  //       message = "Le parcours a été dupliqué. Il est présent dans la liste de vos sessions"
  //       break;
  //     case TypeParcours.PARCOURS_COMPOSE:
  //       message = "Le parcours composé a été dupliqué. Il est présent dans la liste de vos sessions"
  //       break;
  //     case TypeParcours.SESSION:
  //       message = "La session a été dupliquée. Elle est présente dans la liste de vos sessions"
  //   }
  //   return this.http.put(`${this.baseUrl}/sessions/DuplicateSession/${session.idSession}`, null)
  //     .pipe(tap(() => this.notificationService.push(new ToastSuccesModel(`${message}`))));
  // }

  public getFormateursWithBpfStatus(idSession: number): Observable<FormateursBpfListe[]>{
    return this.http.get<FormateursBpfListe[]>(`${this.baseUrl}/SessionFormateur/Get/${idSession}`)
  }

  public changeFormateursStatusBpf(idSession: number, body: ChangeFormateurBpfStatus[]): Observable<any>{
    return this.http.put(`${this.baseUrl}/SessionFormateur/Put/${idSession}`, body).pipe(
      tap((res) => {
        this.notificationService.push(new ToastSuccesModel(res.message));
      }),
    );
  }

  public duplicateSession(session: SessionDetailleeModel, duplicateSession: DuplicateSessionModel): Observable<any> {
    let message: string;
    switch (session.typeParcours) {
      case TypeParcours.PARCOURS:
        message = "Le parcours a été dupliqué. Il est présent dans la liste de vos sessions"
        break;
      case TypeParcours.PARCOURS_COMPOSE:
        message = "Le parcours composé a été dupliqué. Il est présent dans la liste de vos sessions"
        break;
      case TypeParcours.SESSION:
        message = "La session a été dupliquée. Elle est présente dans la liste de vos sessions"
    }
    const { redirect: _redirect, ...body } = duplicateSession;
    return this.http.put<any>(`${this.baseUrl}/sessions/DuplicateSession/${session.idSession}`, body);
  }
}
